<template>
  <div class="maintenance-screen" role="alert" aria-live="assertive">
    <div class="maintenance-card">
      <div class="maintenance-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
      </div>
      <h1 class="maintenance-title">{{ t('maintenance.title') }}</h1>
      <p class="maintenance-description">{{ t('maintenance.description') }}</p>
      <div class="maintenance-status">
        <span class="maintenance-spinner" aria-hidden="true"></span>
        <span>{{ t('maintenance.checking') }}</span>
      </div>
      <button
        type="button"
        class="maintenance-retry"
        :disabled="appStatus.isChecking"
        @click="appStatus.checkBackend()"
      >
        {{ t('maintenance.retry') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStatusStore } from '@/stores/appStatus'
import { useI18n } from '@/composables/useI18n'

const appStatus = useAppStatusStore()
const { t } = useI18n()
</script>

<style scoped>
.maintenance-screen {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: var(--background-primary, #0b0e11);
}

.maintenance-card {
  max-width: 26rem;
  width: 100%;
  text-align: center;
  padding: 2.5rem 2rem;
  border: 1px solid var(--border-primary, rgba(255, 255, 255, 0.08));
  border-radius: var(--radius-lg, 16px);
  background: var(--background-secondary, #14181d);
}

.maintenance-icon {
  width: 3.5rem;
  height: 3.5rem;
  margin: 0 auto 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--primary-blue, #f0b90b);
  background: color-mix(in srgb, var(--primary-blue, #f0b90b) 12%, transparent);
}

.maintenance-icon svg {
  width: 1.75rem;
  height: 1.75rem;
}

.maintenance-title {
  margin: 0 0 0.75rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-primary, #eaecef);
}

.maintenance-description {
  margin: 0 0 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--text-secondary, #a7b1bc);
}

.maintenance-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  font-size: 0.85rem;
  color: var(--text-tertiary, #767f89);
}

.maintenance-spinner {
  width: 0.9rem;
  height: 0.9rem;
  border: 2px solid var(--border-primary, rgba(255, 255, 255, 0.15));
  border-top-color: var(--primary-blue, #f0b90b);
  border-radius: 50%;
  animation: maintenance-spin 0.8s linear infinite;
}

@keyframes maintenance-spin {
  to { transform: rotate(360deg); }
}

.maintenance-retry {
  padding: 0.7rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0b0e11;
  background: var(--primary-blue, #f0b90b);
  border: none;
  border-radius: var(--radius-md, 10px);
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.maintenance-retry:hover:not(:disabled) {
  opacity: 0.85;
}

.maintenance-retry:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
